import Vue from 'vue'
import Api from '@/api'

function coerceCanAccess(value) {
  if (value === true || value === 1 || value === '1' || value === 'true') {
    return true
  }
  if (value === false || value === 0 || value === '0' || value === 'false') {
    return false
  }
  return !!value
}

function parseCanAccessFromResponse(res) {
  if (res && res.data && res.data.can_access !== undefined) {
    return coerceCanAccess(res.data.can_access)
  }
  if (
    res &&
    res.data &&
    res.data.data &&
    res.data.data.can_access !== undefined
  ) {
    return coerceCanAccess(res.data.data.can_access)
  }
  const d = res && res.data && res.data.data
  if (d && typeof d === 'object' && d.data && d.data.can_access !== undefined) {
    return coerceCanAccess(d.data.can_access)
  }
  return false
}

/** Vuex passes a single payload; support legacy numeric tradieId or { tradieId, companyId }. */
function normalizeCheckCanAccessPayload(payload) {
  if (payload == null) {
    return { tradieId: undefined, companyId: undefined }
  }
  if (
    typeof payload === 'object' &&
    !Array.isArray(payload) &&
    'tradieId' in payload
  ) {
    return {
      tradieId: payload.tradieId,
      companyId: payload.companyId,
    }
  }
  return { tradieId: payload, companyId: undefined }
}

/** Dedupe parallel `checkCanAccessForCompany` calls for the same company id. */
const companyCanAccessInflight = {}

export default {
  namespaced: true,

  state: {
    balance: null, // 0
    balanceLoading: false,
    canAccess: false, // false
    /** Per-company tradie access from `tokens/can-access?company_id=` (free tier / trial / token). */
    companyCanAccess: {},
    tokenStatus: null,
  },

  // getters
  getters: {
    // balance
    balance(state) {
      return state.balance
    },
    balanceLoading(state) {
      return state.balanceLoading
    },
    // status
    status(state) {
      return state.status
    },
    // can access
    canAccess(state) {
      return state.canAccess
    },
    companyCanAccessMap(state) {
      return state.companyCanAccess
    },
  },

  // mutrations
  mutations: {
    // set Status
    setStatus(state, status) {
      state.status = status
    },
    // set Balance
    setBalance(state, balance) {
      state.balance = balance
    },
    // set Can Access
    setCanAccess(state, canAccess) {
      state.canAccess = canAccess
    },
    setCompanyCanAccess(state, { companyId, canAccess }) {
      Vue.set(state.companyCanAccess, String(companyId), !!canAccess)
    },
    resetCompanyCanAccess(state) {
      state.companyCanAccess = {}
    },
    // set Loading
    setBalanceLoading(state, loading) {
      state.balanceLoading = loading
    },
  },

  actions: {
    // get balance
    async getBalance({ commit }) {
      return new Promise(async (resolve, reject) => {
        commit('setBalanceLoading', true)
        await Api.get(`tokens/balance`)
          .then((res) => {
            commit(
              'setBalance',
              res.data || { available: 0, total: 0, assigned: 0 },
            )
            commit('setBalanceLoading', false)
            resolve(res)
          })
          .catch((err) => {
            commit('setBalanceLoading', false)
            reject(err.response)
          })
      })
    },
    // asign
    async assign({ commit, dispatch }, tradieId, companyId) {
      // await assign post with commit and
      return new Promise(async (resolve, reject) => {
        // await api post
        await Api.post(`tokens/assign`, {
          tradie_id: tradieId,
          company_id: companyId,
        })
          .then(async (res) => {
            await dispatch('getBalance')
            resolve(res)
            return res.data
          })
          .catch((err) => reject(err.response))
        // with response
      })
    },

    /**
     * Tradie free-tier path requires `company_id` on the query string.
     * Pass `{ tradieId, companyId }` (see NotificationsList / tradie profile).
     * Legacy: dispatch with numeric `tradieId` only → global token rule on the API.
     */
    async checkCanAccess({ commit }, payload) {
      return new Promise(async (resolve, reject) => {
        const { tradieId, companyId } = normalizeCheckCanAccessPayload(payload)
        const params = { tradie_id: tradieId }
        if (companyId != null && companyId !== '') {
          params.company_id = companyId
        }
        await Api.get(`tokens/can-access`, { params })
          .then((res) => {
            commit('setCanAccess', parseCanAccessFromResponse(res))
            resolve(res)
          })
          .catch((err) => reject(err.response))
      })
    },

    /**
     * Per-company tradie access. Backend should return true for free-tier companies,
     * during company trial, or when the tradie has an assigned token.
     */
    async checkCanAccessForCompany({ commit, state }, { tradieId, companyId }) {
      if (tradieId == null || companyId == null) return
      const key = String(companyId)
      if (state.companyCanAccess[key] !== undefined) return
      if (companyCanAccessInflight[key]) return companyCanAccessInflight[key]

      companyCanAccessInflight[key] = (async () => {
        try {
          const res = await Api.get(`tokens/can-access`, {
            params: { tradie_id: tradieId, company_id: companyId },
          })
          commit('setCompanyCanAccess', {
            companyId: key,
            canAccess: parseCanAccessFromResponse(res),
          })
          return res
        } catch (e) {
          commit('setCompanyCanAccess', { companyId: key, canAccess: false })
          throw e
        } finally {
          delete companyCanAccessInflight[key]
        }
      })()

      return companyCanAccessInflight[key]
    },

    // get Status
    async getStatus({ commit }, tokenId) {
      return new Promise(async (resolve, reject) => {
        await Api.get(`/tokens/status`, { params: { token_id: tokenId } })
          .then((res) => {
            commit('setStatus', res.data)
            resolve(res)
          })
          .catch((err) => reject(err.response))
      })
    },

    // get Status by tradie_id
    async getStatusByTradieId({ commit }, tradieId) {
      return new Promise(async (resolve, reject) => {
        await Api.get(`/tokens/status`, { params: { tradie_id: tradieId } })
          .then((res) => {
            commit('setStatus', res.data)
            resolve(res)
          })
          .catch((err) => reject(err.response))
      })
    },

    // purchase
    async purchase({ commit }, quantity) {
      return new Promise(async (resolve, reject) => {
        await Api.post(`tokens/purchase`, { quantity: quantity })
          .then((res) => {
            const data = res.data || {}
            if (data.ok === false) {
              const err = new Error(data.message || 'Purchase failed')
              err.response = { data }
              return reject(err)
            }
            if (data.url) {
              window.location.href = res.data.url
            }
            resolve(res)
          })
          .catch((err) => reject(err))
      })
    },
  },
}
